import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ExerciseTypeContainer } from "./ExerciseTypeContainer";
import { ExerciseItem, getExerciseContent } from "@/services/student/exercise.interfaces";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { EmptyStatePanel } from "@/components/ui/empty-state-panel";
import { SearchInput } from "@/components/ui/search-input";
import {
  GlossaryTermGrid,
  glossaryTopicContentClass,
  glossaryTopicHeadingClass,
  matchesGlossarySearch,
  migrateLegacyGlossaryData,
  sortTopics,
} from "@/components/exercises/glossary";

type GlossaryProps = {
  item: ExerciseItem;
};

export const Glossary: React.FC<GlossaryProps> = ({ item }) => {
  const rawContent = getExerciseContent(item);
  const content = useMemo(() => migrateLegacyGlossaryData(rawContent), [rawContent]);

  const [searchQuery, setSearchQuery] = useState("");
  const [expandedTermIds, setExpandedTermIds] = useState<Set<string>>(() => new Set());

  const normalizedQuery = searchQuery.trim();
  const hasSearch = normalizedQuery.length > 0;

  const filteredTopics = useMemo(() => {
    if (!content.topics?.length) return [];

    if (!hasSearch) return sortTopics(content.topics);

    return sortTopics(content.topics)
      .map((topic) => ({
        ...topic,
        terms: (topic.terms ?? []).filter((term) => matchesGlossarySearch(term, normalizedQuery)),
      }))
      .filter((topic) => topic.terms.length > 0);
  }, [content.topics, hasSearch, normalizedQuery]);

  const filteredTermCount = useMemo(
    () => filteredTopics.reduce((total, topic) => total + topic.terms.length, 0),
    [filteredTopics]
  );

  const defaultTopicId = filteredTopics.length === 1 ? filteredTopics[0]?.id : undefined;

  useEffect(() => {
    if (!hasSearch) return;

    const matchingIds = filteredTopics.flatMap((topic) =>
      topic.terms
        .filter((term) => matchesGlossarySearch(term, normalizedQuery))
        .map((term) => term.id)
    );

    if (matchingIds.length === 0) return;

    setExpandedTermIds((prev) => {
      const next = new Set(prev);
      matchingIds.forEach((id) => next.add(id));
      return next;
    });
  }, [filteredTopics, hasSearch, normalizedQuery]);

  const toggleTerm = useCallback((termId: string) => {
    setExpandedTermIds((prev) => {
      const next = new Set(prev);
      if (next.has(termId)) {
        next.delete(termId);
      } else {
        next.add(termId);
      }
      return next;
    });
  }, []);

  const showHint = filteredTermCount > 0 && !hasSearch;

  return (
    <ExerciseTypeContainer
      item={item}
      title1={item.title || ""}
      title2={content.title}
      exerciseNumber={item.id.toString()}
      hideVerifyButton={true}
    >
      <div className="space-y-4">
        <SearchInput
          placeholder="Buscar termos ou traduções..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          aria-label="Buscar termos ou traduções no glossário"
        />

        {showHint && (
          <p className="text-sm text-foreground/70">
            Toque em um termo para ver a tradução.
          </p>
        )}

        {hasSearch && filteredTermCount === 0 && (
          <EmptyStatePanel role="status" aria-live="polite">
            <p className="text-sm text-foreground/80">
              Nenhum termo encontrado para &quot;{normalizedQuery}&quot;.
            </p>
            <button
              type="button"
              onClick={() => setSearchQuery("")}
              className="mt-3 inline-flex min-h-11 items-center justify-center rounded-md px-3 text-sm font-medium text-foreground underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              Limpar busca
            </button>
          </EmptyStatePanel>
        )}

        {filteredTopics.length > 0 && (
          <>
            {filteredTopics.length === 1 ? (
              <section aria-label={filteredTopics[0].title}>
                {(content.topics?.length ?? 0) > 1 && (
                  <h3 className={glossaryTopicHeadingClass}>{filteredTopics[0].title}</h3>
                )}
                <GlossaryTermGrid
                  terms={filteredTopics[0].terms}
                  expandedTermIds={expandedTermIds}
                  onToggle={toggleTerm}
                />
              </section>
            ) : (
              <Accordion
                type="single"
                collapsible
                defaultValue={defaultTopicId}
                className="w-full"
              >
                {filteredTopics.map((topic) => (
                  <AccordionItem key={topic.id} value={topic.id} className="border-border">
                    <AccordionTrigger
                      className="py-4 text-left hover:no-underline"
                      aria-label={`Tópico ${topic.title} com ${topic.terms.length} ${topic.terms.length === 1 ? "termo" : "termos"}`}
                    >
                      <div className="flex w-full items-center justify-between gap-4 pr-2">
                        <span className="font-semibold text-foreground text-balance">
                          {topic.title}
                        </span>
                        <span className="shrink-0 text-sm text-foreground/70">
                          {topic.terms.length} {topic.terms.length === 1 ? "termo" : "termos"}
                        </span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className={glossaryTopicContentClass}>
                      <GlossaryTermGrid
                        terms={topic.terms}
                        expandedTermIds={expandedTermIds}
                        onToggle={toggleTerm}
                      />
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            )}
          </>
        )}

        {!content.topics?.length && !hasSearch && (
          <EmptyStatePanel>Nenhum termo disponível neste glossário.</EmptyStatePanel>
        )}
      </div>
    </ExerciseTypeContainer>
  );
};
